import { Suspense, useEffect, useRef } from 'react';
import { Banner, Page, PageBody, PageHeader, PageHeaderTitle } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { ObjectAttributesEditor, useObjectAttributes } from '@/features/objects';
import { IdentifierType } from '@/features/workspaces';
import { useAnalytics } from '@/services/analytics';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { OBJECT_ID_KEY } from '@/services/db/object/entity';
import type { ObjectType } from '@/services/db/object-type/entity';
import { useObjectTypes } from '@/services/db/object-type/queries';
import { useCanConfigureObjects } from '@/services/permissions/hooks';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { ObjectTypeResolver } from './object-type-resolver';
import { useCreateObjectFlow } from './use-create-object-flow';

const NEW_TO_OBJECTS_BANNER_THRESHOLD = 50;

const LEAVE_MESSAGE = 'You have unsaved changes.';
const INVALID_LEAVE_MESSAGE =
  'You have unsaved changes. Discard them, or correct the errors on the page to save, to continue.';

type NewObjectResolvedProps = {
  objectType: ObjectType;
  workspaceId: number;
};

function NewObjectResolved({ objectType, workspaceId }: NewObjectResolvedProps) {
  const { track } = useAnalytics();
  const canConfigureObjects = useCanConfigureObjects();
  const environment = useEnvironmentOptional().data?.environment;
  const { data: objectTypes } = useObjectTypes();
  const editor = useObjectAttributes({
    initialAttrs: [{ [OBJECT_ID_KEY]: '' }, { created_at: String(Math.floor(Date.now() / 1000)) }, { name: '' }],
    isNew: true,
  });
  const flow = useCreateObjectFlow({ objectType, workspaceId, editor });

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    void track('objects', 'objectCreateStarted', {});
  }, [track]);

  const save = async () => {
    if (editor.isInvalid || !canConfigureObjects) return;
    await flow.save();
  };

  // The leave modal treats a resolved onSave as "safe to navigate", so a
  // rejected create must reject here — the error is already shown on the page.
  const saveOrThrow = async () => {
    if (editor.isInvalid || !canConfigureObjects) return;
    const didSave = await flow.save();
    if (!didSave) throw new Error('Object create failed');
  };

  const leaveGuard = {
    hasUnsavedChanges: flow.hasUnsavedChanges,
    message: editor.isInvalid ? INVALID_LEAVE_MESSAGE : LEAVE_MESSAGE,
    onSave: saveOrThrow,
    isSaveDisabled: editor.isInvalid || !canConfigureObjects,
    onDiscard: () => editor.reset(),
  };
  useNavigationGuard(leaveGuard);
  useHistoryLeaveBlocker(leaveGuard);

  const objectCount = objectTypes.reduce((acc, type) => acc + (type.object_count ?? 0), 0);
  const singular = objectType.singular_name;
  const article = /^[aeiou]/i.test(singular) ? 'an' : 'a';

  return (
    <>
      <DocumentTitle title={`Add ${singular}`} />
      <Page>
        <PageHeader>
          <PageHeaderTitle>
            Add {article} {singular}
          </PageHeaderTitle>
        </PageHeader>
        <PageBody>
          {objectCount < NEW_TO_OBJECTS_BANNER_THRESHOLD ? (
            <Banner
              variant="information"
              mb="300"
              description="It looks like you're still pretty new to objects. Most folks create objects automatically via an integration or CDP, but if you want to create a couple objects and relationships just to explore, the object_id isn't very important. Once you're integrated, you'd use that to keep objects in sync with your other systems."
            />
          ) : null}

          <ObjectAttributesEditor
            editor={editor}
            sentAttributes={objectType.sent_attributes ?? []}
            hasMoreAttributes={environment?.more_attributes === true}
            isMultiIdentifier={environment?.identifier_type === IdentifierType.MultiIdentifier}
            isSaving={flow.isSaving}
            canConfigureObjects={canConfigureObjects}
            onSave={save}
            onReset={flow.discard}
          />
        </PageBody>
      </Page>
    </>
  );
}

function NewObjectContent() {
  const router = useRouter();

  return (
    <ObjectTypeResolver
      route="/workspaces/$workspaceId/objects/$slug/new"
      extraBreadcrumb={(objectType) => ({ id: 'objects.new', text: `Add ${objectType.singular_name}` })}
    >
      {(objectType, workspaceId) => (
        <ProtectedRoute
          permission="journeys/objects/create"
          redirectTo={() =>
            router.buildLocation({
              to: '/workspaces/$workspaceId/objects/$slug',
              params: { workspaceId, slug: objectType.slug },
            }).href
          }
        >
          <NewObjectResolved objectType={objectType} workspaceId={workspaceId} />
        </ProtectedRoute>
      )}
    </ObjectTypeResolver>
  );
}

export default function NewObjectPage() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <NewObjectContent />
    </Suspense>
  );
}
